import Button from "@/components/button/Button"
import { useAuth } from "@/components/context/AuthProvider"

import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import { SvgIcon } from "@/components/icon"
import { globalFetchPolicy } from "@/components/layout/fetchPolicies"
import { useBreakpoints } from "@/components/screen"
import WebsiteButton from "@/components/website-button/WebsiteButton"
import websiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { useOrganizationsGeneralInfoQuery } from "@/graphql"
import { getAuthToken } from "@/helpers/auth"
import {
  getUserOrgSlug,
  isSignedInToOtherOrg,
  PortalMismatchContent,
  PortalMismatchNextStep,
} from "@/helpers/orgSession"
import { useHydrated } from "@/hooks/useHydrated"
import { getPageNameFromPathname } from "@/pages/website-landing/helpers/getPageNameFromPathname"
import { WAYLEAVE_GUIDE_PATH, websiteRoutes } from "@/router/routes"
import { EASETECH, globalColors } from "@/utilities/global-variables"
import easetechLogo from "../footer/images/logo_company-white.png"
import "./Header.less"

import { Menu, MenuProps, Popover, Skeleton, Space } from "antd"
import { FC, useEffect, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router"

const { Text } = websiteTypography

const items = [
  {
    label: "home",
    key: "home",
  },
  {
    label: "software",
    key: "software",
  },
  {
    label: "one submission",
    key: "one-submission",
  },
  {
    label: "consulting",
    key: "consulting",
  },
  {
    // A static page outside this app: a real link (full page load, followed by search engines), not navigate().
    label: <a href={WAYLEAVE_GUIDE_PATH}>{"wayleave guide"}</a>,
    key: "wayleave-guide",
  },
  {
    label: "contact us",
    key: "contact-us",
  },
  // {
  //   label: "our services",
  //   key: "our-services",
  // },
  // {
  //   label: "industries",
  //   key: "industries",
  // },
  // {
  //   label: 'blog',
  //   key: 'blog',
  // },
]

const Header: FC = () => {
  const { user, isUserDataLoading, hasCheckedAuth } = useAuth()
  const navigate = useNavigate()
  const { website, dashboard } = websiteRoutes
  const params = useParams()
  const currentOrgSlug = params.org || EASETECH
  const { pathname } = useLocation()
  const { openDarkModal } = useModalDarkContext()

  const { isXxl, isXl, isLg } = useBreakpoints()
  // The token lives in localStorage: read it only after hydration, so the prerendered SIGN IN buttons are kept.
  const hasToken = useHydrated() && !!getAuthToken()

  const { currentPageKey, portal } = getPageNameFromPathname(pathname)

  const [pageKey, setPageKey] = useState(currentPageKey)
  const [isPopoverOpen, setPopoverOpen] = useState(false)

  const isEasetech = currentOrgSlug === EASETECH
  // Easetech's logo and colours are in the code, so its header is complete in the prerendered HTML; other
  // organizations' come from the CMS.
  const { data, loading } = useOrganizationsGeneralInfoQuery({
    fetchPolicy: globalFetchPolicy,
    variables: {
      filter: { slug: { eq: currentOrgSlug } },
    },
    skip: isEasetech,
  })

  useEffect(() => {
    setPageKey(currentPageKey)
  }, [currentPageKey])

  const currentOrganization = data?.organizations?.data?.[0]
  const primaryColor = currentOrganization?.attributes?.primaryColor
  const logoUrl = isEasetech
    ? easetechLogo
    : currentOrganization?.attributes?.logoWithCompanyName?.data?.attributes?.url

  const onLogoClick = () => {
    navigate(`/${currentOrgSlug}${website.home}`)
  }

  const onOpenChange = (isOpen: boolean) => {
    setPopoverOpen(isOpen)
  }

  // Signed in to another organization: this portal's dashboard would 404, and signing in here
  // ends that session — so show SIGN IN / REGISTER and explain before anything happens.
  const isSignedInElsewhere = isSignedInToOtherOrg(user, currentOrgSlug)

  /** Returns false (and opens the explanation) when the session belongs to another organization. */
  const guardOtherOrgSession = (nextStep: PortalMismatchNextStep) => {
    if (!isSignedInElsewhere) {
      return true
    }
    setPopoverOpen(false)
    openDarkModal<PortalMismatchContent>({
      type: "portal-mismatch",
      content: { userOrgSlug: getUserOrgSlug(user) ?? "", targetOrgSlug: currentOrgSlug, portal, nextStep },
      isLogo: true,
      width: "width900",
      topOffset: "small",
    })
    return false
  }

  const onSignIn = () => {
    if (!guardOtherOrgSession("login")) return
    navigate(`/${currentOrgSlug}${website.auth}/${portal}/login`, {
      state: { from: location.pathname },
    })
  }

  const onRegister = () => {
    if (!guardOtherOrgSession("register")) return
    navigate(`/${currentOrgSlug}${website.auth}/${portal}/client`, {
      state: { from: location.pathname },
    })
  }

  const onClick: MenuProps["onClick"] = async e => {
    onOpenChange(false)

    if (e.key === "home") {
      navigate(`/${currentOrgSlug}${website.home}`)
    }

    if (e.key === "software") {
      navigate(`/${currentOrgSlug}${website.software}`)
    }

    if (e.key === "one-submission") {
      navigate(`/${currentOrgSlug}${website.channelPartners}`)
    }
    if (e.key === "consulting") {
      navigate(`/${currentOrgSlug}${website.consulting}`)
    }
    if (e.key === "contact-us") {
      handleContactRequestClick()
    }
    // if (e.key === "industries") {
    //   navigate(`/${currentOrgSlug}${website.industries}`)
    // }

    // if (e.key === "our-services") {
    //   navigate(`/${currentOrgSlug}${website.ourServices}`)
    // }

    // if (e.key === 'contact-us') {
    //   await scrollToSection('phone-section')
    // }

    // if (e.key === 'blog') {
    //   navigate(`/${currentOrgSlug}${website.blog}`)
    // }
  }

  const handleContactRequestClick = () => {
    setPopoverOpen(false)
    openDarkModal({
      type: "quick-contact",
      isLogo: true,
    })
  }

  const onGoToDashboard = () => {
    if (!guardOtherOrgSession("dashboard")) return
    navigate(`/${currentOrgSlug}${dashboard.overview}`)
  }

  const content = (
    <div
      className={"burger-popup-content"}
      style={{
        backgroundColor: currentOrgSlug !== EASETECH && primaryColor ? primaryColor : undefined,
      }}
    >
      {currentOrgSlug === EASETECH && (
        <Menu selectedKeys={[pageKey]} items={items} className={"header-menu"} onClick={onClick} />
      )}

      {(hasCheckedAuth && !isUserDataLoading && !user) || isSignedInElsewhere ? (
        <Space
          className={"burger-btn-block"}
          direction={"vertical"}
          style={{ paddingTop: "10px", paddingBottom: "10px", width: "100%" }}
        >
          <WebsiteButton onClick={onSignIn} btnType={"ghost-white"} style={{ marginRight: 8, width: "100%" }}>
            <Text size={"md"}>{"SIGN IN"}</Text>
          </WebsiteButton>

          <WebsiteButton onClick={onRegister} btnType={"primary"} style={{ marginRight: 8, width: "100%" }}>
            <Text size={"md"}>{"REGISTER"}</Text>
          </WebsiteButton>
        </Space>
      ) : (
        <WebsiteButton onClick={onGoToDashboard} style={{ width: "100%" }}>
          {"go to dashboard"}
        </WebsiteButton>
      )}
    </div>
  )

  return (
    <>
      <div className={"main-header"} style={{ backgroundColor: primaryColor || globalColors?.defaultDarkPrimaryColor }}>
        <div className={"header-content"}>
          <div
            className={`header-logo-wrapper${isEasetech ? " clickable" : ""}`}
            onClick={isEasetech ? onLogoClick : undefined}
          >
            {loading ? (
              <Skeleton loading={true} paragraph={{ rows: 1 }} />
            ) : (
              <img src={logoUrl} alt={isEasetech ? "Easetech" : "logo"} />
            )}
          </div>
          <div
            className={"header-content-wrapper"}
            style={currentOrgSlug !== EASETECH ? { justifyContent: "end" } : undefined}
          >
            {currentOrgSlug === EASETECH && (isXxl || isXl || isLg) && (
              <Menu
                selectedKeys={[pageKey]}
                items={items}
                mode={"horizontal"}
                className={"header-menu"}
                onClick={onClick}
                // overflowedIndicator={null}
              />
            )}

            {isXxl || isXl || isLg ? (
              <div className={"btn-block"}>
                {!hasToken || isSignedInElsewhere ? (
                  <>
                    <WebsiteButton onClick={onSignIn} btnType={"ghost-white"} style={{ marginRight: 8, width: "100%" }}>
                      <Text size={"md"}>{"SIGN IN"}</Text>
                    </WebsiteButton>

                    <WebsiteButton onClick={onRegister} btnType={"primary"} style={{ marginRight: 8, width: "100%" }}>
                      <Text size={"md"}>{"REGISTER"}</Text>
                    </WebsiteButton>
                  </>
                ) : (
                  <WebsiteButton onClick={onGoToDashboard}>{"to dashboard"}</WebsiteButton>
                )}
              </div>
            ) : (
              <Popover
                placement={"bottomRight"}
                title={null}
                content={content}
                trigger={"click"}
                showArrow={false}
                overlayClassName={"popover-burger-container"}
                overlayStyle={{
                  borderRadius: "7px",
                  padding: 0,
                  border: `1px solid ${
                    currentOrgSlug !== EASETECH && primaryColor ? primaryColor : "@primary-dark-blue"
                  }`,
                }}
                onOpenChange={onOpenChange}
                open={isPopoverOpen}
              >
                <Button
                  className={"burger-button"}
                  btnType={"icon"}
                  icon={<SvgIcon type={isPopoverOpen ? "close-modal" : "burger-icon"} />}
                />
              </Popover>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default Header
